"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { PIPELINE, PIPELINE_TAGS, SECTION_IDS } from "./content";
import { cn } from "@/app/utils/className";

const ADVANCE_MS = 2800;

export const HowItWorks = () => {
  const [stage, setStage] = useState(0);
  // Any deliberate interaction hands control to the visitor for good.
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    if (!autoplay) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = window.setInterval(
      () => setStage((current) => (current + 1) % PIPELINE.length),
      ADVANCE_MS
    );
    return () => window.clearInterval(timer);
  }, [autoplay]);

  const select = (index: number) => {
    setStage(index);
    setAutoplay(false);
  };

  return (
    <section
      id={SECTION_IDS.howItWorks}
      className="dc-section border-t border-white/[.07] bg-ink-2"
    >
      <div className="dc-shell">
        <div className="mb-[clamp(48px,6vw,80px)] grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] items-end gap-9">
          <h2 className="m-0 text-[clamp(34px,5vw,82px)] font-bold leading-[.98] tracking-[-.04em]">
            Data to decision,
            <br />
            in one pass.
          </h2>
          <p className="m-0 max-w-[44ch] text-[17px] leading-[1.7] text-mist-3">
            Observations, climate records, elevation and your own data enter one
            reconciled archive. The engine does the rest — and the answer lands
            where the decision gets made.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
          {PIPELINE.map((step, index) => {
            const isActive = index === stage;
            const isReached = index <= stage;

            return (
              <button
                key={step.num}
                type="button"
                aria-pressed={isActive}
                onClick={() => select(index)}
                onMouseEnter={() => select(index)}
                onFocus={() => select(index)}
                className={cn(
                  "flex min-w-0 cursor-pointer flex-col gap-[18px] border-l border-white/[.12] px-[clamp(14px,1.4vw,22px)] pb-1 text-left transition-opacity duration-[450ms]",
                  isActive ? "opacity-100" : "opacity-[.55]"
                )}
              >
                <div className="relative h-[clamp(90px,9vw,130px)] overflow-hidden">
                  <Image
                    src={step.img}
                    alt={step.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 20vw"
                    className={cn(
                      "object-cover transition-[filter] duration-[450ms]",
                      isActive ? "saturate-100" : "saturate-[.2]"
                    )}
                  />
                  <div
                    className="absolute inset-0 transition-[background] duration-[450ms]"
                    style={{
                      background: isActive
                        ? "linear-gradient(180deg, rgba(3,12,22,.1), rgba(3,12,22,.45))"
                        : "linear-gradient(180deg, rgba(3,12,22,.55), rgba(3,12,22,.8))",
                    }}
                  />
                </div>

                <div
                  className={cn(
                    "h-0.5 transition-colors duration-[450ms]",
                    isReached ? "bg-mint" : "bg-white/[.14]"
                  )}
                />

                <div>
                  <div
                    className={cn(
                      "dc-mono mb-2.5 text-[12px] tracking-[.18em] transition-colors duration-[450ms]",
                      isReached ? "text-mint" : "text-mist-4"
                    )}
                  >
                    {step.num}
                  </div>
                  <div className="mb-2.5 text-[clamp(16px,1.5vw,21px)] font-semibold leading-[1.15] tracking-[-.018em] text-paper">
                    {step.title}
                  </div>
                  <div className="text-[15px] leading-[1.65] text-mist-3">
                    {step.body}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Auto-advance affordance — carried over from v1 of the design */}
        <div className="dc-mono mt-5 flex items-center gap-3.5 text-[12px] uppercase tracking-[.18em] text-mist-5">
          <span>{autoplay ? "Auto-advancing" : "Manual"}</span>
          <span className="h-px flex-1 bg-white/[.12]" />
          <span className="text-mist-4">
            {PIPELINE[stage].num} — {PIPELINE[stage].title}
          </span>
        </div>

        <div className="dc-mono mt-[clamp(44px,6vw,80px)] flex flex-wrap gap-x-10 gap-y-3.5 border-t border-white/[.14] pt-7 text-[13px] uppercase tracking-[.2em] text-mist-4">
          {PIPELINE_TAGS.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </div>
    </section>
  );
};
